// Property context for shared state across all components
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/api/client';

const PropertyContext = createContext(null);

export function PropertyProvider({ children }) {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProperties = useCallback(async () => {
    // Only load if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const stored = localStorage.getItem('selectedProperty');
      if (stored) {
        setSelectedProperty(JSON.parse(stored));
      }

      const props = await api.getProperties();
      setProperties(props);

      // Auto-select if only one property
      if (props.length === 1 && !stored) {
        localStorage.setItem('selectedProperty', JSON.stringify(props[0]));
        setSelectedProperty(props[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const selectProperty = useCallback((property) => {
    localStorage.setItem('selectedProperty', JSON.stringify(property));
    setSelectedProperty(property);
  }, []);

  const clearSelection = useCallback(() => {
    localStorage.removeItem('selectedProperty');
    setSelectedProperty(null);
    setProperties([]);
  }, []);

  const value = {
    properties,
    selectedProperty,
    loading,
    error,
    selectProperty,
    clearSelection,
    refreshProperties: loadProperties,
    needsSelection: !loading && properties.length > 1 && !selectedProperty,
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
}

export function useProperty() {
  const ctx = useContext(PropertyContext);
  if (!ctx) throw new Error('useProperty must be used within PropertyProvider');
  return ctx;
}
